import React from 'react';
import { motion } from 'framer-motion';
import { Settings, Trash2, ExternalLink, Package, Users } from 'lucide-react';

const statusBadge = (overallStatus, isConnected) => {
  if (!isConnected) {
    return {
      label: 'Available',
      className: 'bg-white text-gray-400 border-gray-200',
    };
  }
  switch (overallStatus) {
    case 'active':
      return {
        label: 'Connected',
        className: 'bg-green-50 text-green-600 border-green-100',
      };
    case 'syncing':
      return {
        label: 'Syncing',
        className: 'bg-blue-50 text-blue-600 border-blue-100',
      };
    case 'sync_failed':
      return {
        label: 'Sync failed',
        className: 'bg-red-50 text-red-600 border-red-100',
      };
    case 'connected_not_listening':
      return {
        label: 'No webhooks',
        className: 'bg-amber-50 text-amber-700 border-amber-100',
      };
    case 'disconnected':
      return {
        label: 'Disconnected',
        className: 'bg-gray-50 text-gray-500 border-gray-200',
      };
    default:
      return {
        label: overallStatus?.replace(/_/g, ' ') || 'Needs attention',
        className: 'bg-amber-50 text-amber-700 border-amber-100',
      };
  }
};

const WooCommerceCard = ({ account, onConnect, onManage, onDisconnect }) => {
  const isConnected = !!account;
  const storeUrl = account?.store_url || '';
  const status = account?.overall_status;
  const syncStatus = account?.sync?.status;
  const productsSynced = account?.sync?.products_synced ?? 0;
  const customersSynced = account?.sync?.customers_synced ?? 0;
  const badge = statusBadge(status, isConnected);

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative p-5 sm:p-6 rounded-[1.5rem] flex flex-col h-full border ${
        isConnected
          ? 'bg-white border-gray-100 shadow-sm hover:shadow-lg hover:-translate-y-1'
          : 'bg-gray-50/50 border-gray-200 hover:bg-white'
      }`}
    >
      <div className="flex justify-between items-start mb-4">
        <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center overflow-hidden border border-gray-100 shadow-sm p-1.5">
          <img
            src="https://cdn.simpleicons.org/woocommerce/96588A"
            alt="WooCommerce"
            className="w-full h-full object-contain"
          />
        </div>
        <div
          className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider border ${
            badge.className
          }`}
        >
          {badge.label}
        </div>
      </div>

      <div className="mb-6 flex-1">
        {isConnected ? (
          <div className="space-y-4">
            <div>
              <h3 className="text-lg font-black text-gray-900 tracking-tight truncate">
                {storeUrl.replace(/^https?:\/\//, '')}
              </h3>
              <p className="text-[10px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">
                {syncStatus === 'syncing' ? 'Sync in progress' : 'WooCommerce store'}
              </p>
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-3 p-3 bg-gray-50/50 border border-gray-100 rounded-xl">
                <div className="w-10 h-10 rounded-lg bg-[#96588A]/10 flex items-center justify-center">
                  <Package className="h-5 w-5 text-[#96588A]" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900">{productsSynced} products synced</p>
                  {account?.sync?.last_synced_at && (
                    <p className="text-xs text-gray-500">
                      Last synced {new Date(account.sync.last_synced_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-gray-50/50 border border-gray-100 rounded-xl">
                <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
                  <Users className="h-5 w-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-gray-900">{customersSynced} customers synced</p>
                  {account?.sync?.customers_last_synced_at && (
                    <p className="text-xs text-gray-500">
                      Updated{' '}
                      {new Date(account.sync.customers_last_synced_at).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <h3 className="text-xl font-black text-gray-900 tracking-tight">WooCommerce</h3>
            <p className="text-sm text-gray-500 leading-relaxed font-medium">
              Sync your product catalog and answer store questions with AI.
            </p>
          </div>
        )}
      </div>

      <div className="mt-auto pt-5 border-t border-gray-100 flex items-center gap-2">
        {isConnected ? (
          <>
            <button
              onClick={() => onManage(account.integration_id)}
              className="flex-1 px-4 py-2.5 bg-black hover:bg-gray-800 text-white text-[11px] font-bold rounded-xl flex items-center justify-center gap-2"
            >
              <Settings className="w-4 h-4" /> Manage
            </button>
            <button
              onClick={() => onDisconnect(account.integration_id)}
              className="p-2.5 bg-red-50 hover:bg-red-100 text-red-600 rounded-xl border border-red-100"
              title="Disconnect"
            >
              <Trash2 className="h-4 w-4" />
            </button>
          </>
        ) : (
          <button
            onClick={onConnect}
            className="w-full px-4 py-3 bg-black text-white hover:bg-gray-800 text-xs font-bold rounded-xl flex items-center justify-center gap-2"
          >
            Connect WooCommerce <ExternalLink className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
    </motion.div>
  );
};

export default WooCommerceCard;
